/**
 * Catalogue du poste en memoire pour l'ecran de vente : lu une fois dans
 * IndexedDB (magasin `catalog_items`, rempli par la PR 1.4), puis indexe pour
 * qu'un scan trouve son article en temps constant (acceptation : la ligne
 * s'ajoute en moins de 100 ms).
 *
 *   - par sku : le champ `codes` de store/catalog.js toStoredItem (scan par
 *     sku seulement, decision du 2026-10-18), avec les formes UPC-A / EAN-13
 *     du meme code (domain/barcode.js formesDuCode) ;
 *   - par SKU au poids (`weight_sku`) pour les etiquettes a prix integre ; un
 *     article au poids trouve par son sku demande son etiquette ;
 *   - recherche par nom : sans accents ni casse, tous les mots doivent y etre.
 */
import { requestToPromise } from '../store/idb';
import { STORES } from '../store/schema';
import { TYPES_SCAN, classerScan } from '../domain/barcode';
import { articleVendable, estAuPoids } from './panier';

export const RESULTATS_MAX = 12;

/** Texte sans accents ni casse, espaces reduits. */
export function normaliser(texte) {
    return String(texte || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Index d'un tableau d'articles (tels que ranges par toStoredItem).
 * @param {object[]} articles
 */
export function indexerCatalogue(articles) {
    const parCode = new Map();
    const parSkuPoids = new Map();
    const noms = [];
    for (const a of articles) {
        if (!articleVendable(a)) continue;
        const codes = Array.isArray(a.codes) ? a.codes : (typeof a.sku === 'string' && a.sku !== '' ? [a.sku] : []);
        for (const c of codes) if (!parCode.has(c)) parCode.set(c, a);
        if (typeof a.weight_sku === 'string' && a.weight_sku !== '' && !parSkuPoids.has(a.weight_sku)) parSkuPoids.set(a.weight_sku, a);
        noms.push({ article: a, nom: normaliser(a.name), sku: typeof a.sku === 'string' ? a.sku.toLowerCase() : '' });
    }
    return { parCode, parSkuPoids, noms, taille: noms.length };
}

/** Lit tout le catalogue du poste et l'indexe. */
export async function chargerCatalogue(db) {
    const tx = db.transaction(STORES.CATALOG_ITEMS, 'readonly');
    const articles = await requestToPromise(tx.objectStore(STORES.CATALOG_ITEMS).getAll());
    return indexerCatalogue(articles);
}

export const RESULTATS_SCAN = Object.freeze({
    ARTICLE: 'ARTICLE',
    POIDS: 'POIDS',
    ETIQUETTE_REQUISE: 'ETIQUETTE_REQUISE',
    INCONNU: 'INCONNU',
    ILLISIBLE: 'ILLISIBLE',
    VIDE: 'VIDE',
});

/**
 * Trouve l'article d'un code lu.
 * @param {ReturnType<typeof indexerCatalogue>} index
 * @param {string} texte
 * @param {{ prefixe?: string|null }} options prefixe des etiquettes au poids (catalog_meta)
 * @returns {{ resultat: string, code: string, article?: object, priceCents?: number }}
 */
export function trouverParCode(index, texte, { prefixe = null } = {}) {
    const scan = classerScan(texte, { prefixe });
    if (scan.type === TYPES_SCAN.VIDE) return { resultat: RESULTATS_SCAN.VIDE, code: '' };
    if (scan.type === TYPES_SCAN.POIDS) {
        const article = index.parSkuPoids.get(scan.weightSku);
        if (article) return { resultat: RESULTATS_SCAN.POIDS, code: scan.code, article, priceCents: scan.priceCents };
    }
    // Un code a 13 chiffres au prefixe du poids peut aussi etre le sku exact d'un article.
    for (const forme of scan.type === TYPES_SCAN.CODE ? scan.formes : [scan.code]) {
        const article = index.parCode.get(forme);
        if (article && estAuPoids(article)) return { resultat: RESULTATS_SCAN.ETIQUETTE_REQUISE, code: scan.code, article };
        if (article) return { resultat: RESULTATS_SCAN.ARTICLE, code: scan.code, article };
    }
    if (scan.type === TYPES_SCAN.POIDS_ILLISIBLE) return { resultat: RESULTATS_SCAN.ILLISIBLE, code: scan.code };
    return { resultat: RESULTATS_SCAN.INCONNU, code: scan.code };
}

/**
 * Recherche par nom (ou debut de sku) : tous les mots saisis doivent etre
 * dans le nom. Les noms qui COMMENCENT par la saisie passent devant.
 * @returns {object[]} au plus `max` articles
 */
export function chercherParNom(index, texte, max = RESULTATS_MAX) {
    const q = normaliser(texte);
    if (q.length < 2) return [];
    const mots = q.split(' ');
    const debut = [];
    const ailleurs = [];
    for (const n of index.noms) {
        if (mots.every((m) => n.nom.includes(m)) || (n.sku && n.sku.startsWith(q))) {
            (n.nom.startsWith(q) ? debut : ailleurs).push(n.article);
            if (debut.length >= max) break;
        }
    }
    return [...debut, ...ailleurs].slice(0, max);
}
